import { put, call, all, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects'
import { store as reduxStore } from 'index'
import {
  listAgents,
  updateAgent,
  deleteAgent,
  listTools,
  deleteTool,
  getAgentCreateForm,
  getAgentEditForm,
  getAgentIntegrations,
  getToolCreateForm,
  getToolEditForm,
  submitForm,
  ByoAgent,
  ByoIntegration,
  ByoTool,
  ByoToolType,
  ChatApp,
  DynamicFormSchema,
} from 'services/byoAgents'
import actions from './actions'
import { AiAgentsState } from './reducers'

/** Bring Your Own Agents sagas (Agents / Actions / Tools tabs and their forms). */

type Saga<P> = { payload: P }
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SagaWorker = (action: any) => any
// The shared action/tool workers take their type ahead of the action, as `takeLatest` passes it.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ToolSagaWorker = (type: ByoToolType, action: any) => any

interface RootState {
  apps: { active: ChatApp }
  aiAgents: AiAgentsState
}
const getRoot = (): RootState => reduxStore.getState() as unknown as RootState
const activeApp = (data?: ChatApp): ChatApp => data ?? getRoot().apps.active

const notify = (msg: string, type: 'success' | 'error') =>
  window.triggerNotification?.(msg, type)

interface AppPayload {
  data?: ChatApp
}

// Actions and tools share one worker; only the state keys and copy differ.
const TOOL_KEYS = {
  action: {
    list: 'aiActionsList',
    loading: 'areAiActionsLoading',
    createForm: 'aiActionCreateForm',
    createFormLoading: 'isAiActionCreateFormLoading',
    editForm: 'aiActionEditForm',
    editFormLoading: 'isAiActionEditFormLoading',
    label: 'AI Action',
    plural: 'AI Actions',
  },
  tool: {
    list: 'aiToolsList',
    loading: 'areAiToolsLoading',
    createForm: 'aiToolCreateForm',
    createFormLoading: 'isAiToolCreateFormLoading',
    editForm: 'aiToolEditForm',
    editFormLoading: 'isAiToolEditFormLoading',
    label: 'AI Tool',
    plural: 'AI Tools',
  },
} as const

/* ─── Agents ─────────────────────────────────────────────────────────────── */

export function* AI_AGENTS_LIST({ payload = {} }: Saga<AppPayload>) {
  yield put({ type: actions.SET_STATE, payload: { areAiAgentsLoading: true } })
  try {
    const agents: ByoAgent[] = yield call(listAgents, activeApp(payload.data))
    yield put({
      type: actions.SET_STATE,
      payload: { aiAgentsList: Array.isArray(agents) ? agents : [], areAiAgentsLoading: false },
    })
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { aiAgentsList: [], areAiAgentsLoading: false } })
    notify('Failed to fetch AI Agents', 'error')
  }
}

interface AgentIdPayload extends AppPayload {
  agentId: string
}

export function* DELETE_AI_AGENT({ payload }: Saga<AgentIdPayload>) {
  const withoutAgent = (ids: string[] = []) => ids.filter(id => id !== payload.agentId)
  yield put({
    type: actions.SET_STATE,
    payload: {
      deletingAgentIds: [...((getRoot().aiAgents.deletingAgentIds as string[]) || []), payload.agentId],
    },
  })

  try {
    yield call(deleteAgent, activeApp(payload.data), payload.agentId)
    const state = getRoot().aiAgents
    yield put({
      type: actions.SET_STATE,
      payload: {
        aiAgentsList: ((state.aiAgentsList as ByoAgent[]) || []).filter(
          agent => agent.uid !== payload.agentId,
        ),
        deletingAgentIds: withoutAgent(state.deletingAgentIds as string[]),
      },
    })
    notify('AI Agent deleted successfully', 'success')
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: { deletingAgentIds: withoutAgent(getRoot().aiAgents.deletingAgentIds as string[]) },
    })
    notify('Failed to delete AI Agent', 'error')
  }
}

interface AgentTogglePayload extends AgentIdPayload {
  isActive: boolean
}

export function* EDIT_AI_AGENT({ payload }: Saga<AgentTogglePayload>) {
  const withoutAgent = (ids: string[] = []) => ids.filter(id => id !== payload.agentId)
  yield put({
    type: actions.SET_STATE,
    payload: {
      updatingAgentIds: [...((getRoot().aiAgents.updatingAgentIds as string[]) || []), payload.agentId],
    },
  })

  try {
    yield call(updateAgent, activeApp(payload.data), payload.agentId, { isActive: payload.isActive })
    const state = getRoot().aiAgents
    yield put({
      type: actions.SET_STATE,
      payload: {
        // Only the toggled flag is merged, never the rest of the action payload.
        aiAgentsList: ((state.aiAgentsList as ByoAgent[]) || []).map(agent =>
          agent.uid === payload.agentId ? { ...agent, isActive: payload.isActive } : agent,
        ),
        updatingAgentIds: withoutAgent(state.updatingAgentIds as string[]),
      },
    })
    notify('AI Agent status updated successfully', 'success')
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: { updatingAgentIds: withoutAgent(getRoot().aiAgents.updatingAgentIds as string[]) },
    })
    notify('Failed to update AI Agent', 'error')
  }
}

/* ─── Agent forms ────────────────────────────────────────────────────────── */

export function* GET_AI_AGENT_CREATE_FORM({ payload = {} }: Saga<AppPayload>) {
  yield put({
    type: actions.SET_STATE,
    payload: { isAiAgentCreateFormLoading: true, aiAgentCreateForm: null },
  })
  try {
    const form: DynamicFormSchema = yield call(getAgentCreateForm, activeApp(payload.data))
    yield put({
      type: actions.SET_STATE,
      payload: { aiAgentCreateForm: form, isAiAgentCreateFormLoading: false },
    })
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: { aiAgentCreateForm: null, isAiAgentCreateFormLoading: false },
    })
    notify('Failed to load the agent form', 'error')
  }
}

export function* GET_AI_AGENT_EDIT_FORM({ payload }: Saga<AgentIdPayload>) {
  yield put({
    type: actions.SET_STATE,
    payload: { isAiAgentEditFormLoading: true, aiAgentEditForm: null },
  })
  try {
    const form: DynamicFormSchema = yield call(
      getAgentEditForm,
      activeApp(payload.data),
      payload.agentId,
    )
    yield put({
      type: actions.SET_STATE,
      payload: { aiAgentEditForm: form, isAiAgentEditFormLoading: false },
    })
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: { aiAgentEditForm: null, isAiAgentEditFormLoading: false },
    })
    notify('Failed to load the agent form', 'error')
  }
}

export function* GET_AI_AGENT_INTEGRATIONS({ payload = {} }: Saga<AppPayload>) {
  yield put({
    type: actions.SET_STATE,
    payload: { isAiAgentIntegrationsLoading: true, aiAgentIntegrations: [] },
  })
  try {
    const integrations: ByoIntegration[] = yield call(getAgentIntegrations, activeApp(payload.data))
    yield put({
      type: actions.SET_STATE,
      payload: { aiAgentIntegrations: integrations || [], isAiAgentIntegrationsLoading: false },
    })
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: { aiAgentIntegrations: [], isAiAgentIntegrationsLoading: false },
    })
    notify('Failed to load agent platforms', 'error')
  }
}

/* ─── Actions / Tools ────────────────────────────────────────────────────── */

export function* listToolsWorker(type: ByoToolType, { payload = {} }: Saga<AppPayload>) {
  const keys = TOOL_KEYS[type]
  yield put({ type: actions.SET_STATE, payload: { [keys.loading]: true } })
  try {
    const tools: ByoTool[] = yield call(listTools, activeApp(payload.data), type)
    yield put({
      type: actions.SET_STATE,
      payload: { [keys.list]: Array.isArray(tools) ? tools : [], [keys.loading]: false },
    })
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { [keys.list]: [], [keys.loading]: false } })
    notify(`Failed to fetch ${keys.plural}`, 'error')
  }
}

interface ToolNamePayload extends AppPayload {
  toolName: string
}

export function* deleteToolWorker(type: ByoToolType, { payload }: Saga<ToolNamePayload>) {
  const keys = TOOL_KEYS[type]
  const withoutTool = (names: string[] = []) => names.filter(name => name !== payload.toolName)
  yield put({
    type: actions.SET_STATE,
    payload: {
      deletingToolNames: [
        ...((getRoot().aiAgents.deletingToolNames as string[]) || []),
        payload.toolName,
      ],
    },
  })

  try {
    yield call(deleteTool, activeApp(payload.data), payload.toolName)
    const state = getRoot().aiAgents
    yield put({
      type: actions.SET_STATE,
      payload: {
        [keys.list]: ((state[keys.list] as ByoTool[]) || []).filter(
          tool => tool.name !== payload.toolName,
        ),
        deletingToolNames: withoutTool(state.deletingToolNames as string[]),
      },
    })
    notify(`${keys.label} deleted successfully`, 'success')
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: { deletingToolNames: withoutTool(getRoot().aiAgents.deletingToolNames as string[]) },
    })
    notify(`Failed to delete ${keys.label}`, 'error')
  }
}

export function* toolCreateFormWorker(type: ByoToolType, { payload = {} }: Saga<AppPayload>) {
  const keys = TOOL_KEYS[type]
  yield put({
    type: actions.SET_STATE,
    payload: { [keys.createFormLoading]: true, [keys.createForm]: null },
  })
  try {
    const form: DynamicFormSchema = yield call(getToolCreateForm, activeApp(payload.data), type)
    yield put({
      type: actions.SET_STATE,
      payload: { [keys.createForm]: form, [keys.createFormLoading]: false },
    })
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: { [keys.createForm]: null, [keys.createFormLoading]: false },
    })
    notify(`Failed to load the ${keys.label} form`, 'error')
  }
}

export function* toolEditFormWorker(type: ByoToolType, { payload }: Saga<ToolNamePayload>) {
  const keys = TOOL_KEYS[type]
  yield put({
    type: actions.SET_STATE,
    payload: { [keys.editFormLoading]: true, [keys.editForm]: null },
  })
  try {
    const form: DynamicFormSchema = yield call(
      getToolEditForm,
      activeApp(payload.data),
      type,
      payload.toolName,
    )
    yield put({
      type: actions.SET_STATE,
      payload: { [keys.editForm]: form, [keys.editFormLoading]: false },
    })
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: { [keys.editForm]: null, [keys.editFormLoading]: false },
    })
    notify(`Failed to load the ${keys.label} form`, 'error')
  }
}

/* ─── Form submit ────────────────────────────────────────────────────────── */

const LIST_ACTION = {
  agents: actions.AI_AGENTS_LIST,
  actions: actions.AI_ACTIONS_LIST,
  tools: actions.AI_TOOLS_LIST,
} as const

interface SubmitPayload extends AppPayload {
  submitDetails: DynamicFormSchema['submitDetails']
  values: Record<string, unknown>
  /** Which list to refetch once the server has accepted the form. */
  refresh: keyof typeof LIST_ACTION
  onSuccess?: () => void
}

interface ChatApiError {
  response?: { data?: { error?: { devMessage?: string } } }
}

export function* SUBMIT_BYO_FORM({ payload }: Saga<SubmitPayload>) {
  yield put({ type: actions.SET_STATE, payload: { isByoFormSubmitting: true } })
  try {
    yield call(submitForm, activeApp(payload.data), payload.submitDetails, payload.values)
    yield put({ type: actions.SET_STATE, payload: { isByoFormSubmitting: false } })
    notify('Submitted successfully', 'success')
    yield put({ type: LIST_ACTION[payload.refresh], payload: {} })
    if (typeof payload.onSuccess === 'function') payload.onSuccess()
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { isByoFormSubmitting: false } })
    notify((error as ChatApiError)?.response?.data?.error?.devMessage || 'Submission failed', 'error')
  }
}

export default function* byoAgentsSaga() {
  yield all([
    // Lists are latest-only: a slow earlier response must not overwrite a newer one.
    takeLatest(actions.AI_AGENTS_LIST, AI_AGENTS_LIST as SagaWorker),
    takeEvery(actions.AI_AGENT_DELETE, DELETE_AI_AGENT as SagaWorker),
    takeEvery(actions.AI_AGENT_UPDATE, EDIT_AI_AGENT as SagaWorker),
    takeLatest(actions.GET_AI_AGENT_CREATE_FORM, GET_AI_AGENT_CREATE_FORM as SagaWorker),
    takeLatest(actions.GET_AI_AGENT_EDIT_FORM, GET_AI_AGENT_EDIT_FORM as SagaWorker),
    takeLatest(actions.GET_AI_AGENT_INTEGRATIONS, GET_AI_AGENT_INTEGRATIONS as SagaWorker),
    takeLatest(actions.AI_ACTIONS_LIST, listToolsWorker as ToolSagaWorker, 'action'),
    takeLatest(actions.AI_TOOLS_LIST, listToolsWorker as ToolSagaWorker, 'tool'),
    takeEvery(actions.AI_ACTION_DELETE, deleteToolWorker as ToolSagaWorker, 'action'),
    takeEvery(actions.AI_TOOL_DELETE, deleteToolWorker as ToolSagaWorker, 'tool'),
    takeLatest(actions.GET_AI_ACTION_CREATE_FORM, toolCreateFormWorker as ToolSagaWorker, 'action'),
    takeLatest(actions.GET_AI_TOOL_CREATE_FORM, toolCreateFormWorker as ToolSagaWorker, 'tool'),
    takeLatest(actions.GET_AI_ACTION_EDIT_FORM, toolEditFormWorker as ToolSagaWorker, 'action'),
    takeLatest(actions.GET_AI_TOOL_EDIT_FORM, toolEditFormWorker as ToolSagaWorker, 'tool'),
    // Leading-only so a double click on Save can't create the record twice.
    takeLeading(actions.SUBMIT_BYO_FORM, SUBMIT_BYO_FORM as SagaWorker),
  ])
}
